<template>
  <div
    class="account-info"
  >
    <template v-if="closeAccountPage">
      <NoPage />
    </template>
    <template v-else>
      <div class="header">
        <span class="title">
          {{ $t('account.title') }}
        </span>
        <GobitoLink
          href="https://trendyol.akademi.net/portal/course/hesap-bilgilerim-137325"
          lms-href="/ELearning?TrainingId=2635"
          @click="onGobito"
        >
          {{ $t('account.gobitoLinkText') }}
        </GobitoLink>
      </div>
      <ScCard class="page-container">
        <template #header>
          <Tabs
            class="account-tabs"
            :summary="tabs"
            :type="activeTab"
            @handleClick="tabChange"
          />
        </template>
        <template #body>
          <Component
            :is="activeTab"
            :editable="isEditable"
            class="page-body"
          />
        </template>
      </ScCard>
    </template>
  </div>
</template>

<script>
import Tabs from '@/components/Shared/Tabs';
import UserInformation from '@/components/Info/UserInformation';
import AccountInformation from '@/components/Info/AccountInformation/Index';
import AddressInformation from '@/components/Info/AddressInformation';
import BankInformation from '@/components/Info/BankInformation';
import IntegrationInformation from '@/components/Info/IntegrationInformation';
import SellerContactsInformation from '@/components/Info/SellerContactsInformation';
import CommunicationSettings from '@/components/Info/CommunicationSettings';
import ScCard from '@trendyol/sc-ui-kit/ScCard';
import SupplierService from '@/services/SupplierService';
import { remoteConfig } from '@/services/';
import GobitoLink from '@/components/Shared/GobitoLink';
import HolidayModeSettings from '@/components/Info/HolidayModeSettings';
import NoPage from '@/components/Shared/NoPage';

export default {
  name: 'Info',
  components: {
    NoPage,
    GobitoLink,
    Tabs,
    ScCard,
    AccountInformation,
    CommunicationSettings,
    UserInformation,
    BankInformation,
    AddressInformation,
    IntegrationInformation,
    SellerContactsInformation,
    HolidayModeSettings,
  },
  data () {
    return {
      allTabs: [
        {
          label: this.$t('account.tabs.userInformation'),
          type: 'UserInformation',
          isVisible: () => true,
        },
        {
          label: this.$t('account.tabs.communicationSettings'),
          type: 'CommunicationSettings',
          isVisible: () => remoteConfig.getConfigValueByKey('APPLY_SPR375') && this.$route.params.unsubscribe,
        },
        {
          label: this.$t('account.tabs.sellerContactsInformation'),
          type: 'sellerContactsInformation',
          isVisible: () => SupplierService.hasAuthority('ROLE_ADMIN'),
        },
        {
          label: this.$t('account.tabs.accountInformation'),
          type: 'AccountInformation',
          isVisible: () => SupplierService.hasAuthority('SELLER_ACCOUNT_FIRM_INFO_VIEW'),
        },
        {
          label: this.$t('account.tabs.bankInformation'),
          type: 'BankInformation',
          isVisible: () => SupplierService.hasAuthority('SELLER_ACCOUNT_BANK_INFO_VIEW'),
        },
        {
          label: this.$t('account.tabs.addressInformation'),
          type: 'AddressInformation',
          isVisible: () => SupplierService.hasAuthority('SELLER_ADDRESS_INFO_VIEW'),
        },
        {
          label: this.$t('account.tabs.integrationInformation'),
          type: 'integrationInformation',
          isVisible: () => SupplierService.hasAuthority('ROLE_ADMIN'),
        },
        {
          label: this.$t('account.tabs.holidayMode'),
          type: 'holidayModeSettings',
          isVisible: () => {
            return SupplierService.isActive() && SupplierService.isMaster();
          },
        },
      ],
      activeTab: 'UserInformation',
    };
  },
  computed: {
    closeAccountPage () {
      return remoteConfig.getConfigValueByKey('CLOSE_ACCOUNT_PAGE');
    },
    isEditable () {
      if (this.activeTab === 'UserInformation') {
        return remoteConfig.getConfigValueByKey('IS_USER_PASSWORD_CHANGE_ENABLED', false);
      } else if (this.activeTab === 'AccountInformation') {
        return remoteConfig.getConfigValueByKey('IS_SUPPLIER_NAME_CHANGE_ENABLED', false) &&
          SupplierService.isActive() &&
          SupplierService.isMaster();
      } else if (this.activeTab === 'integrationInformation') {
        return SupplierService.isMaster() && SupplierService.isActive();
      } else if (this.activeTab === 'CommunicationSettings') {
        return SupplierService.isMaster();
      } else if (this.activeTab === 'BankInformation') {
        return SupplierService.isMaster() && SupplierService.isActive() &&
          remoteConfig.getConfigValueByKey('UPDATE_IBAN', false);
      } else if (this.activeTab === 'holidayModeSettings') {
        return !remoteConfig.getConfigValueByKey('IS_LOCKDOWN_OPTION_ENABLED') && SupplierService.isMaster() && SupplierService.isActive();
      } else if (this.activeTab === 'AddressInformation') {
        return SupplierService.isMaster() && SupplierService.isActive();
      }
      return true;
    },
    tabs () {
      return this.allTabs.filter((tab) => tab.isVisible());
    },
  },
  methods: {
    onGobito () {
      this.$gtm.trackEvent({
        event: 'GAEvent',
        eventCategory: 'Onboarding - Hesabım',
        eventAction: 'Eğitim Yönlendirmesi',
        eventLabel: SupplierService.getSupplierId(),
      });
    },
    tabChange (type) {
      this.$gtm.trackEvent({
        'content-name': 'AccountInfoPage',
        'event': 'content-view',
        'tab': type,
      });

      this.activeTab = type;
      this.$router.replace({
        ...this.$router.currentRoute,
        query: { tab: type },
      });
    },

    determineActiveTab () {
      const queryTab = this.$route.query.tab;

      const isValidTab = this.tabs.some(tab => tab.type === queryTab);

      if (isValidTab) {
        this.activeTab = queryTab;
      }
    },
  },

  created () {
    this.determineActiveTab();
    this.$gtm.trackEvent({
      'content-name': 'AccountInfoPage',
      'event': 'content-view',
      'tab': this.activeTab,
    });
  },

  watch: {
    $route () {
      this.determineActiveTab();
    },
  },
};
</script>
<style lang="scss">
  .account-info * {
    box-sizing: border-box;
  }
</style>
<style lang="scss" scoped>
  .account-info{
    padding: 32px;
    max-width: 100%;
    margin: 0 auto;
    height: 100%;
    width: 100%;

    ::v-deep .sc-card-header {
      overflow-x: auto;
    }

    ::v-deep .page-body .sc-card-header{
      overflow: unset !important;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .title{
        font-size: 30px;
        font-weight: 300;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        color: #273142;
      }
    }

    .page-container{
      margin-top: 30px;
      ::v-deep .sc-card{
        padding: 0px;
      }
    }

    .page-body{
      padding: 20px 30px;
    }
  }
</style>
